/* MollieCheckoutModal — iter914 (was iter913)
 *
 * Inline method-picker + iDEAL issuer-select. Removes 1–2 clicks from
 * the Mollie hosted checkout for methods that support pre-selection
 * (Mollie docs: "By specifying `method` and `issuer` on create, the
 * shopper is sent directly to the payment step") — measurable
 * conversion lift for iDEAL / Bancontact traffic.
 *
 * States
 * ──────
 *   loading    → fetching methods from GET /api/payments/mollie/methods
 *   pick       → step 1: user picks a payment method
 *   issuer     → step 2: only for iDEAL — user picks their bank
 *   redirect   → step 3: create intent + redirect to Mollie
 *   error      → any hard failure
 *
 * Methods that skip the issuer step (single-flow): card, bancontact,
 * paypal, banktransfer, kbc, belfius, klarna, sofort, applepay, gpay,
 * etc. iDEAL is the only Mollie method with a bank picker as of Feb 2026.
 *
 * If the merchant's account exposes only ONE method (rare edge case),
 * we skip the pick step and go straight to the confirmation.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Loader2, ExternalLink, X, ShieldCheck, ArrowLeft, Check,
  CreditCard, Building2, Wallet, ChevronRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REGION_MAP = { t1: "premium", eu: "eu_standard", bg: "bg_balkans", t3: "emerging" };
const ISSUER_METHODS = new Set(["ideal"]);   // methods where users pick a bank

// Local BG/EN labels — Mollie returns English descriptions.
const METHOD_LABELS_BG = {
  creditcard:   "Кредитна / дебитна карта",
  card:         "Кредитна / дебитна карта",
  ideal:        "iDEAL",
  bancontact:   "Bancontact",
  kbc:          "KBC / CBC",
  belfius:      "Belfius Pay",
  paypal:       "PayPal",
  banktransfer: "Банков превод",
  klarna:       "Klarna",
  sofort:       "SOFORT",
  applepay:     "Apple Pay",
  googlepay:    "Google Pay",
  eps:          "EPS",
  giropay:      "Giropay",
  przelewy24:   "Przelewy24",
};

const METHOD_FALLBACK_ICON = {
  creditcard:   CreditCard,
  card:         CreditCard,
  applepay:     CreditCard,
  googlepay:    CreditCard,
  banktransfer: Building2,
  paypal:       Wallet,
  klarna:       Wallet,
};

export default function MollieCheckoutModal({
  plan,
  billingPeriod,
  planIdOverride,
  market,
  onClose,
  isDark,
}) {
  const [step, setStep] = useState("loading");     // loading | pick | issuer | redirect | error
  const [methods, setMethods] = useState([]);      // available methods
  const [method, setMethod] = useState(null);
  const [issuers, setIssuers] = useState([]);
  const [issuer, setIssuer] = useState(null);
  const [error, setError] = useState(null);

  const token =
    localStorage.getItem("token") ||
    sessionStorage.getItem("nutramilo_access_token") ||
    "";
  const auth = { Authorization: `Bearer ${token}` };

  const planId =
    planIdOverride ||
    ((plan?.name || "pro").toLowerCase() === "clinic"
      ? "clinician"
      : (plan?.name || "pro").toLowerCase());
  const period =
    billingPeriod === "annual"
      ? "annual"
      : billingPeriod === "one_time"
        ? "one_time"
        : "monthly";
  const region =
    REGION_MAP[market] || (period === "one_time" ? undefined : "eu_standard");

  // ── Fetch enabled methods on open ─────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/payments/mollie/methods`);
        if (cancelled) return;
        const list = data.methods || [];
        setMethods(list);
        if (list.length === 0) {
          // No methods enabled → go straight to Mollie hosted picker.
          startPayment(null, null);
        } else if (list.length === 1) {
          const only = list[0].id;
          if (ISSUER_METHODS.has(only)) {
            setMethod(only);
            await loadIssuers(only);
            setStep("issuer");
          } else {
            startPayment(only, null);
          }
        } else {
          setStep("pick");
        }
      } catch (e) {
        // Silent fall-through: proceed WITHOUT a preselected method so
        // Mollie hosted checkout still works.
        if (!cancelled) {
          console.warn("[mollie] methods fetch failed, using hosted picker", e);
          startPayment(null, null);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadIssuers = async (m) => {
    try {
      const { data } = await axios.get(
        `${API}/payments/mollie/issuers?method=${encodeURIComponent(m)}`
      );
      setIssuers(data.issuers || []);
      return data.issuers || [];
    } catch {
      setIssuers([]);
      return [];
    }
  };

  const pickMethod = async (m) => {
    setMethod(m);
    if (ISSUER_METHODS.has(m)) {
      setStep("loading");
      const list = await loadIssuers(m);
      if (list.length > 0) {
        setStep("issuer");
      } else {
        // Method has no issuers → just pass method and go.
        startPayment(m, null);
      }
    } else {
      startPayment(m, null);
    }
  };

  const pickIssuer = (i) => {
    setIssuer(i);
    startPayment(method, i);
  };

  const startPayment = async (m, i) => {
    setStep("redirect");
    setError(null);
    try {
      const body = { plan_id: planId, billing_period: period };
      if (region) body.region = region;
      if (m) body.method = m;
      if (i) body.issuer = i;
      const { data } = await axios.post(`${API}/payments/mollie/intent`, body, {
        headers: auth,
      });
      try { sessionStorage.setItem("mollie_last_tx_id", data.tx_id); } catch { /* ignore */ }
      if (!data.checkout_url) throw new Error("Няма URL за плащане от Mollie");
      // Small delay so users can see the "Пренасочваме…" state.
      setTimeout(() => { window.location.href = data.checkout_url; }, 400);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Грешка при създаване на плащане";
      setError(msg);
      setStep("error");
      toast.error(msg);
    }
  };

  const goBack = () => {
    if (step === "issuer") {
      setIssuer(null);
      setStep("pick");
    } else if (step === "error") {
      setError(null);
      setStep(methods.length > 1 ? "pick" : "loading");
    }
  };

  // ── Rendering helpers ─────────────────────────────────────────────
  const cardBg = isDark ? "bg-gray-900 text-gray-100" : "bg-white text-gray-900";
  const tileBg = isDark
    ? "bg-gray-800 border-gray-700 hover:bg-gray-750"
    : "bg-white border-slate-200 hover:border-[#0EA5E9] hover:bg-sky-50";

  const planName = plan?.name || planId;
  const periodLabel =
    period === "one_time" ? "One-time unlock" : period === "annual" ? "Годишен" : "Месечен";

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      onClick={onClose}
      data-testid="mollie-checkout-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative w-full max-w-md rounded-2xl shadow-2xl overflow-hidden ${cardBg}`}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            {(step === "issuer" || step === "error") && (
              <button
                onClick={goBack}
                className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Back"
                data-testid="mollie-modal-back"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <div>
              <div className="text-[11px] uppercase tracking-wider text-gray-500">
                Plan
              </div>
              <div className="text-lg font-semibold">
                {planName} · {periodLabel}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close"
            data-testid="mollie-modal-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ── STEP: loading ─────────────────────────────────────── */}
        {step === "loading" && (
          <div className="p-10 text-center" data-testid="mollie-loading">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#0EA5E9]" />
            <p className="text-sm text-gray-500 mt-2">Подготвяме плащането…</p>
          </div>
        )}

        {/* ── STEP: pick method ─────────────────────────────────── */}
        {step === "pick" && (
          <div className="p-5 space-y-2" data-testid="mollie-method-picker">
            <div className="text-[13px] font-semibold text-gray-500 mb-2 px-1">
              Изберете начин на плащане
            </div>
            {methods.map((m) => {
              const Icon = METHOD_FALLBACK_ICON[m.id];
              return (
                <button
                  key={m.id}
                  onClick={() => pickMethod(m.id)}
                  data-testid={`mollie-method-${m.id}`}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all ${tileBg}`}
                >
                  {m.image_2x || m.image_1x ? (
                    <img
                      src={m.image_2x || m.image_1x}
                      alt=""
                      className="w-8 h-8 object-contain"
                      loading="lazy"
                    />
                  ) : Icon ? (
                    <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-gray-500" />
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700" />
                  )}
                  <div className="flex-1 text-left">
                    <div className="text-sm font-semibold">
                      {METHOD_LABELS_BG[m.id] || m.description}
                    </div>
                    {ISSUER_METHODS.has(m.id) && (
                      <div className="text-[11px] text-gray-500">
                        Директно към вашата банка
                      </div>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </button>
              );
            })}
            <div className="pt-2 flex items-center gap-2 text-[11px] text-gray-500 px-1">
              <ShieldCheck className="w-3.5 h-3.5" />
              PCI-DSS L1 · SSL secured · GDPR compliant · Mollie B.V.
            </div>
          </div>
        )}

        {/* ── STEP: pick issuer (iDEAL only) ────────────────────── */}
        {step === "issuer" && (
          <div className="p-5" data-testid="mollie-issuer-picker">
            <div className="text-[13px] font-semibold text-gray-500 mb-3 px-1">
              Изберете вашата банка
            </div>
            <div className="max-h-[400px] overflow-y-auto grid grid-cols-2 gap-2">
              {issuers.map((iss) => (
                <button
                  key={iss.id}
                  onClick={() => pickIssuer(iss.id)}
                  data-testid={`mollie-issuer-${iss.id}`}
                  className={`flex items-center gap-2 p-2.5 rounded-xl border transition-all ${tileBg}`}
                >
                  {iss.image_2x || iss.image_1x ? (
                    <img
                      src={iss.image_2x || iss.image_1x}
                      alt=""
                      className="w-6 h-6 object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-6 h-6 rounded bg-gray-100 dark:bg-gray-700" />
                  )}
                  <span className="text-xs font-semibold truncate">{iss.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── STEP: redirect ────────────────────────────────────── */}
        {step === "redirect" && (
          <div className="p-10 text-center" data-testid="mollie-redirect">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#0EA5E9]" />
            <p className="text-sm text-gray-500 mt-2">
              Пренасочваме към{" "}
              {method
                ? METHOD_LABELS_BG[method] || "Mollie"
                : "Mollie"}
              …
            </p>
            {issuer && (
              <p className="text-xs text-gray-400 mt-1">
                <Check className="w-3 h-3 inline mr-1 text-emerald-500" />
                Избрана банка потвърдена
              </p>
            )}
          </div>
        )}

        {/* ── STEP: error ───────────────────────────────────────── */}
        {step === "error" && (
          <div className="p-6 text-center space-y-3" data-testid="mollie-error">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={goBack} className="flex-1" data-testid="mollie-error-retry">
                Опитай отново
              </Button>
              <Button variant="ghost" onClick={onClose} className="flex-1" data-testid="mollie-error-close">
                Затвори
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
